/**
 * VendorAcceptInvite.tsx — Public accept-invite screen
 *
 * Rendered OUTSIDE BrowserRouter via conditional early return in App.tsx.
 * Routes: /vendor/accept-invite/:token  — magic link (token present)
 *          /vendor/accept-invite             — bare path (no token, code-entry landing)
 *
 * Flow (token present):
 *   1. Extract token from window.location.pathname on mount
 *   2. GET /api/vendor/accept-invite/:token → show invite details
 *   3. "Accept Invitation" → POST → store JWT in localStorage → redirect to /v4/vendor/dashboard
 *   4. Fallback: 6-digit code entry panel
 *
 * Flow (no token — bare path from invite email):
 *   Renders DirectCodeScreen immediately — no loading, no GET call.
 *
 * Cannot use useNavigate (no Router context) — uses window.location.href for redirect.
 */

import { useState, useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { fetchInviteByToken, acceptInvite, acceptInviteByCode, VendorInviteData } from '../../api/vendor'
import { TOKEN_KEY } from '../../api/client'

// ── Helpers ─────────────────────────────────────────────────────────────────

function extractToken(): string {
  const match = window.location.pathname.match(/^\/vendor\/accept-invite\/(.+)$/)
  return match?.[1] ?? ''
}

// ── Types ────────────────────────────────────────────────────────────────────

type ScreenState = 'loading' | 'ready' | 'accepting' | 'accepted' | 'expired' | 'error' | 'code-entry'

// ── Code Entry sub-component ─────────────────────────────────────────────────

interface CodeEntryPanelProps {
  code: string
  setCode: (v: string) => void
  codeError: string | null
  codeAccepting: boolean
  onSubmit: () => void
}

function CodeEntryPanel({ code, setCode, codeError, codeAccepting, onSubmit }: CodeEntryPanelProps) {
  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <p className="text-xs text-gray-400 text-center mb-3">
        Enter the 6-digit code from your invite email
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-center text-xl font-mono tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
        />
        <button
          onClick={onSubmit}
          disabled={codeAccepting || code.length !== 6}
          className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm"
        >
          {codeAccepting ? '…' : 'Go →'}
        </button>
      </div>
      {codeError && (
        <p className="mt-2 text-xs text-red-600 text-center">{codeError}</p>
      )}
    </div>
  )
}

// ── Layout wrapper ───────────────────────────────────────────────────────────

function PageCenter({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      {children}
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 max-w-md w-full p-8">
      {children}
    </div>
  )
}

// ── State screens ────────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <PageCenter>
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto mb-4" />
        <p className="text-sm text-gray-400">Loading your invitation…</p>
      </div>
    </PageCenter>
  )
}

function ExpiredScreen() {
  return (
    <PageCenter>
      <Card>
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invite Expired</h1>
          <p className="text-sm text-gray-500">
            This invite link has expired. Contact your contractor to send a new invite.
          </p>
        </div>
      </Card>
    </PageCenter>
  )
}

function AlreadyAcceptedScreen({ token }: { token: string }) {
  const queryClient = useQueryClient()

  useEffect(() => {
    // Re-issue JWT — backend accept endpoint is idempotent.
    // Vendor may have a stale or missing token if they revisit the magic link.
    acceptInvite(token)
      .then((result) => {
        localStorage.setItem(TOKEN_KEY, result.token)
        queryClient.invalidateQueries({ queryKey: ['vendor-my-projects'] })
        queryClient.invalidateQueries({ queryKey: ['vendor-documents'] })
        queryClient.invalidateQueries({ queryKey: ['vendor-profile'] })
      })
      .catch(() => {
        // If re-issue fails, existing session may still be valid — navigate anyway.
      })
      .finally(() => {
        window.location.href = '/vendor/dashboard'
      })
  }, [token, queryClient])

  return (
    <PageCenter>
      <Card>
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Already Accepted</h1>
          <p className="text-sm text-gray-500 mb-6">Taking you to your dashboard…</p>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto" />
        </div>
      </Card>
    </PageCenter>
  )
}

interface ErrorScreenProps {
  message: string | null
  showCodeEntry: boolean
  onShowCode: () => void
  code: string
  setCode: (v: string) => void
  codeError: string | null
  codeAccepting: boolean
  onCodeSubmit: () => void
}

function ErrorScreen({ message, showCodeEntry, onShowCode, code, setCode, codeError, codeAccepting, onCodeSubmit }: ErrorScreenProps) {
  return (
    <PageCenter>
      <Card>
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invite Not Found</h1>
          <p className="text-sm text-gray-500 mb-6">
            {message ?? 'This invite link is invalid or has been removed.'}
          </p>
          {!showCodeEntry && (
            <button
              onClick={onShowCode}
              className="text-sm text-orange-500 hover:text-orange-600 font-medium underline"
            >
              Have a 6-digit code? Enter it here
            </button>
          )}
          {showCodeEntry && (
            <CodeEntryPanel
              code={code} setCode={setCode}
              codeError={codeError} codeAccepting={codeAccepting}
              onSubmit={onCodeSubmit}
            />
          )}
        </div>
      </Card>
    </PageCenter>
  )
}


// ── Direct code entry (bare /vendor/accept-invite — no token in URL) ─────────

interface DirectCodeScreenProps {
  code: string
  setCode: (v: string) => void
  codeError: string | null
  codeAccepting: boolean
  onCodeSubmit: () => void
}

function DirectCodeScreen({ code, setCode, codeError, codeAccepting, onCodeSubmit }: DirectCodeScreenProps) {
  return (
    <PageCenter>
      <Card>
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Enter your invite code</h1>
          <p className="text-sm text-gray-500">
            Enter the 6-digit code from your invite email to accept your invitation.
          </p>
        </div>
        <CodeEntryPanel
          code={code} setCode={setCode}
          codeError={codeError} codeAccepting={codeAccepting}
          onSubmit={onCodeSubmit}
        />
      </Card>
    </PageCenter>
  )
}

// ── Main screen ──────────────────────────────────────────────────────────────

export default function VendorAcceptInvite() {
  const token = extractToken()
  const queryClient = useQueryClient()

  const [screenState, setScreenState] = useState<ScreenState>('loading')
  const [invite, setInvite] = useState<VendorInviteData | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [showCodeEntry, setShowCodeEntry] = useState(false)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  const [codeAccepting, setCodeAccepting] = useState(false)

  useEffect(() => {
    if (!token) {
      setScreenState('code-entry')
      return
    }
    fetchInviteByToken(token)
      .then((data) => {
        setInvite(data)
        if (data.status === 'expired') setScreenState('expired')
        else if (data.status === 'accepted') setScreenState('accepted')
        else setScreenState('ready')
      })
      .catch((err: unknown) => {
        setScreenState('error')
        setErrorMsg((err as Error).message ?? 'Failed to load invite.')
      })
  }, [token])

  const handleAccept = useCallback(async () => {
    if (!token) return
    setScreenState('accepting')
    setErrorMsg(null)
    try {
      const result = await acceptInvite(token)
      localStorage.setItem(TOKEN_KEY, result.token)
      // Invalidate all vendor query cache so VendorDashboardScreen
      // fetches fresh data (not the stale empty array from pre-acceptance).
      await queryClient.invalidateQueries({ queryKey: ['vendor-my-projects'] })
      await queryClient.invalidateQueries({ queryKey: ['vendor-documents'] })
      await queryClient.invalidateQueries({ queryKey: ['vendor-profile'] })
      window.location.href = '/vendor/dashboard'
    } catch (err: unknown) {
      setScreenState('ready')
      setErrorMsg((err as Error).message ?? 'Failed to accept invite. Please try again.')
    }
  }, [token])

  const handleCodeAccept = useCallback(async () => {
    if (code.length !== 6) {
      setCodeError('Please enter your 6-digit code.')
      return
    }
    setCodeAccepting(true)
    setCodeError(null)
    try {
      const result = await acceptInviteByCode(code)
      localStorage.setItem(TOKEN_KEY, result.token)
      // Invalidate vendor cache so dashboard fetches fresh project list
      await queryClient.invalidateQueries({ queryKey: ['vendor-my-projects'] })
      await queryClient.invalidateQueries({ queryKey: ['vendor-documents'] })
      await queryClient.invalidateQueries({ queryKey: ['vendor-profile'] })
      window.location.href = '/vendor/dashboard'
    } catch (err: unknown) {
      setCodeAccepting(false)
      setCodeError((err as Error).message ?? 'Invalid code. Please try again.')
    }
  }, [code])

  // ── State screens ──
  if (screenState === 'loading') return <LoadingScreen />
  if (screenState === 'expired') return <ExpiredScreen />
  if (screenState === 'accepted') return <AlreadyAcceptedScreen token={token} />
  if (screenState === 'code-entry') return (
    <DirectCodeScreen
      code={code} setCode={setCode}
      codeError={codeError} codeAccepting={codeAccepting}
      onCodeSubmit={handleCodeAccept}
    />
  )
  if (screenState === 'error') return (
    <ErrorScreen
      message={errorMsg}
      showCodeEntry={showCodeEntry}
      onShowCode={() => setShowCodeEntry(true)}
      code={code} setCode={setCode}
      codeError={codeError} codeAccepting={codeAccepting}
      onCodeSubmit={handleCodeAccept}
    />
  )

  // ── Ready / Accepting ──
  const isAccepting = screenState === 'accepting'
  const gcName = invite!.contractor_company_name || invite!.contractor_name

  return (
    <PageCenter>
      <Card>
        {/* Header — who invited them */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-sm text-gray-400 mb-1">You've been invited by</p>
          <h1 className="text-2xl font-bold text-gray-900">{gcName}</h1>
          {invite!.contractor_company_name && invite!.contractor_name !== invite!.contractor_company_name && (
            <p className="text-sm text-gray-400 mt-0.5">via {invite!.contractor_name}</p>
          )}
        </div>

        {/* Project card */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Project</p>
          <p className="text-base font-semibold text-gray-900">{invite!.project_name}</p>
        </div>

        {/* Vendor details */}
        <div className="space-y-2.5 mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Company</span>
            <span className="text-gray-900 font-medium">{invite!.vendor_company_name}</span>
          </div>
          {invite!.vendor_trade && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Trade</span>
              <span className="text-gray-900 font-medium">{invite!.vendor_trade}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Contact</span>
            <span className="text-gray-900 font-medium">{invite!.vendor_contact_name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Email</span>
            <span className="text-gray-900 font-medium text-right break-all">{invite!.vendor_email}</span>
          </div>
        </div>

        {/* Inline error */}
        {errorMsg && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {errorMsg}
          </div>
        )}

        {/* Primary CTA */}
        <button
          data-testid="accept-invite-btn"
          onClick={handleAccept}
          disabled={isAccepting}
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-semibold py-3 rounded-xl transition-colors text-base mb-3"
        >
          {isAccepting ? 'Accepting…' : 'Accept Invitation →'}
        </button>

        {/* 6-digit code fallback */}
        {!showCodeEntry ? (
          <button
            onClick={() => setShowCodeEntry(true)}
            className="w-full text-sm text-gray-400 hover:text-gray-600 py-1.5 transition-colors"
          >
            Have a 6-digit code instead?
          </button>
        ) : (
          <CodeEntryPanel
            code={code} setCode={setCode}
            codeError={codeError} codeAccepting={codeAccepting}
            onSubmit={handleCodeAccept}
          />
        )}
      </Card>
    </PageCenter>
  )
}
